<template>
  <div class="grid gap-1.5 text-sm text-text-secondary">
    <span :id="labelId">{{ messages.label }}</span>
    <AppSelect
      :model-value="selectedMode"
      :options="options"
      :disabled="disabled"
      :aria-labelledby="labelId"
      @update:model-value="onSelection"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import type { AppLocale } from '@/shared/i18n'
import { useI18n, useLocaleMessages } from '@/shared/composables/useI18n'
import AppSelect from './AppSelect.vue'
import type { AppSelectOption } from './AppSelect.vue'
import { languageSelectorEn } from './locales/en.ts'
import { languageSelectorRu } from './locales/ru.ts'

export interface LanguageSelectorProps {
  readonly disabled?: boolean
}

type LanguageSelection = 'automatic' | AppLocale

withDefaults(defineProps<LanguageSelectorProps>(), {
  disabled: false,
})

const catalog = defineLocaleCatalog({
  en: languageSelectorEn,
  ru: languageSelectorRu,
})
const i18n = useI18n()
const messages = useLocaleMessages(catalog)
const labelId = `language-selector-label-${useId()}`
const selectedMode = computed<LanguageSelection>(() =>
  i18n.preference.value.mode === 'automatic'
    ? 'automatic'
    : i18n.preference.value.locale,
)
const options = computed<ReadonlyArray<AppSelectOption>>(() => [
  {
    value: 'automatic',
    text: messages.value.automaticDescription,
  },
  { value: 'en', text: messages.value.options.en },
  { value: 'ru', text: messages.value.options.ru },
])

function isLanguageSelection(value: string | number | null): value is LanguageSelection {
  return value === 'automatic' || value === 'en' || value === 'ru'
}

function onSelection(value: string | number | null): void {
  if (!isLanguageSelection(value)) return
  if (value === 'automatic') {
    i18n.useAutomaticLocale()
    return
  }
  i18n.setExplicitLocale(value)
}
</script>
